"use client";

import { useEffect, useRef, useState } from "react";

// Top-of-page loading bar for full-page navigations and initial document
// loads.
//
// This project uses full browser navigation (see components/ui/Link.tsx) — no
// SPA router events to listen to. We cover two scenarios:
//
// 1. Direct URL hit / refresh: SSR ships the bar already visible at a small
//    starting width, then on hydration we drive the asymptotic animation
//    until `window.load` fires (full document + subresources ready), then
//    fill-to-100 and fade out.
// 2. Click / form-submit / programmatic navigation: the document-level
//    listeners restart the bar after it has faded, then the new document
//    again ships the bar visible and the cycle repeats.
//
// `pageshow` with `event.persisted` covers bfcache restores (Safari/Firefox
// Back) where the same DOM comes back and we want the bar gone.
const INITIAL = 0.08;

export function PageProgressBar() {
  // SSR-rendered values: bar is visible from the very first paint so a direct
  // URL hit shows feedback immediately, before React even hydrates.
  const [running, setRunning] = useState(true);
  const progressRef = useRef(INITIAL);
  const barRef = useRef<HTMLDivElement | null>(null);
  const ariaPctRef = useRef(Math.round(INITIAL * 100));
  const rafRef = useRef<number | null>(null);
  const finishT1Ref = useRef<number | null>(null);
  const finishT2Ref = useRef<number | null>(null);
  const reducedMotionRef = useRef(false);

  /**
   * Write the bar's position straight to the DOM.
   *
   * This used to be `setProgress(next)` inside the rAF loop — a React state
   * update, and therefore a full reconciliation of this component, on EVERY
   * ANIMATION FRAME, from hydration until `window.load` fires. That window is
   * exactly the one Total Blocking Time is measured over, on every page on the
   * site, and on a 4x-throttled mobile CPU it was the largest piece of
   * first-party main-thread work on the page.
   *
   * Nothing about the animation changes: the element is already promoted to
   * its own compositor layer by `willChange: "transform, opacity"`, so a
   * transform write is composited off the main thread. React never needed to
   * be in this loop — it was only ever here to move one number.
   *
   * `aria-valuenow` is written only when the rounded percentage actually
   * changes, rather than 60x a second, which is both cheaper and far less
   * abusive to a screen reader.
   *
   * This is safe against React's own renders (`running` flipping) because the
   * JSX below carries CONSTANT values for `transform` and `aria-valuenow`:
   * React 19 diffs style and attributes per key and only writes what changed,
   * so it never touches either after hydration — paint() is their sole owner.
   * Keep those two JSX values constant; making either depend on state would
   * put React back in the loop and fight this function.
   */
  const paint = (value: number) => {
    progressRef.current = value;
    const node = barRef.current;
    if (!node) return;
    node.style.transform = `scaleX(${value})`;
    const pct = Math.round(value * 100);
    if (pct !== ariaPctRef.current) {
      ariaPctRef.current = pct;
      node.setAttribute("aria-valuenow", String(pct));
    }
  };

  useEffect(() => {
    reducedMotionRef.current = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const tick = () => {
      // Asymptotic curve: each frame closes ~1.5% of the remaining gap to
      // 0.9. Quick at first, slows as it approaches the cap.
      paint(progressRef.current + (0.9 - progressRef.current) * 0.015);
      rafRef.current = requestAnimationFrame(tick);
    };

    const animate = () => {
      if (rafRef.current !== null) return;
      if (reducedMotionRef.current) {
        paint(0.3);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const start = () => {
      // Already running (initial load, or a prior click that hasn't completed)
      // — keep the existing momentum.
      if (progressRef.current > 0) return;
      paint(INITIAL);
      setRunning(true);
      animate();
    };

    const finish = () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      paint(1);
      // Hold the filled bar briefly so users register completion, then fade
      // out, then reset so a future click can re-trigger.
      finishT1Ref.current = window.setTimeout(() => {
        setRunning(false);
        finishT2Ref.current = window.setTimeout(() => {
          paint(0);
        }, 220);
      }, 180);
    };

    // Initial-load handling: animate the SSR-rendered bar until `load` fires.
    // If we hydrate after `load` has already fired (cached / instant page),
    // skip straight to finish so the visible bar fades out cleanly.
    if (document.readyState === "complete") {
      finish();
    } else {
      animate();
      window.addEventListener("load", finish, { once: true });
    }

    const isNavigatingClick = (e: MouseEvent, anchor: HTMLAnchorElement) => {
      if (e.defaultPrevented) return false;
      if (e.button !== 0) return false;
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return false;
      if (anchor.target && anchor.target !== "_self") return false;
      if (anchor.hasAttribute("download")) return false;

      const href = anchor.getAttribute("href");
      if (!href) return false;
      if (/^(mailto:|tel:|javascript:|sms:)/i.test(href)) return false;
      if (href.startsWith("#")) return false;

      let url: URL;
      try {
        url = new URL(anchor.href, window.location.href);
      } catch {
        return false;
      }
      if (url.origin !== window.location.origin) return false;
      if (url.pathname === window.location.pathname && url.search === window.location.search) {
        return false;
      }
      return true;
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as Element | null;
      const anchor = target?.closest?.("a");
      if (!anchor) return;
      if (!isNavigatingClick(e, anchor as HTMLAnchorElement)) return;
      start();
    };

    const onSubmit = (e: SubmitEvent) => {
      // React calls preventDefault() on submit for server-action forms
      // (action={fn}) — those stay on the same document, so the bar would
      // never finish. Skip them; only animate for navigating posts.
      if (e.defaultPrevented) return;
      start();
    };
    const onPageHide = () => start();
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        if (finishT1Ref.current !== null) clearTimeout(finishT1Ref.current);
        if (finishT2Ref.current !== null) clearTimeout(finishT2Ref.current);
        paint(0);
        setRunning(false);
      }
    };

    document.addEventListener("click", onClick, true);
    // Bubble phase so React's submit handler (which preventDefaults
    // server-action forms) runs before we decide whether to start.
    document.addEventListener("submit", onSubmit, false);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);

    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("submit", onSubmit, false);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("load", finish);
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      if (finishT1Ref.current !== null) clearTimeout(finishT1Ref.current);
      if (finishT2Ref.current !== null) clearTimeout(finishT2Ref.current);
    };
  }, []);

  return (
    <div
      ref={barRef}
      role="progressbar"
      aria-hidden={running ? undefined : true}
      aria-label={running ? "Page loading" : undefined}
      aria-valuemin={0}
      aria-valuemax={100}
      // Constant on purpose: the first-paint value, after which `paint()` owns
      // it (see the note there). `aria-hidden` already takes the finished bar
      // out of the accessibility tree, so there is nothing to gain by removing
      // this on `running`, and doing so would make React and paint() fight.
      aria-valuenow={Math.round(INITIAL * 100)}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: "2.5px",
        transformOrigin: "left",
        transform: `scaleX(${INITIAL})`,
        background: "var(--color-brand-500)",
        boxShadow: running ? "0 0 10px var(--color-brand-500)" : "none",
        opacity: running ? 1 : 0,
        transition: "transform 120ms linear, opacity 200ms ease-out",
        zIndex: 9999,
        pointerEvents: "none",
        willChange: "transform, opacity",
      }}
    />
  );
}
